import React, { useState } from 'react';
import './CreateProject.css';

interface Deliverable {
  id: string;
  value: string;
}

const ProjectForm: React.FC = () => {
  const [projectTitle, setProjectTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [deadline, setDeadline] = useState<string>('');
  const [deliverables, setDeliverables] = useState<Deliverable[]>([
    { id: '1', value: '' }
  ]);
  const [teamSize, setTeamSize] = useState<string>('4');

  const addDeliverable = () => {
    const newId = Date.now().toString();
    setDeliverables([...deliverables, { id: newId, value: '' }]);
  };

  const updateDeliverable = (id: string, value: string) => {
    setDeliverables(
      deliverables.map(d => (d.id === id ? { ...d, value } : d))
    );
  };

  const handlePublish = () => {
    console.log('Publishing project...', {
      projectTitle,
      description,
      category,
      deadline,
      deliverables: deliverables.filter(d => d.value.trim()),
      teamSize
    });
  };

  const handleSaveDraft = () => {
    console.log('Saving as draft...');
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Unsaved changes will be lost.')) {
      setProjectTitle('');
      setDescription('');
      setCategory('');
      setDeadline('');
      setDeliverables([{ id: '1', value: '' }]);
      setTeamSize('4');
    }
  };

  return (
    <div className="admin-group-create-project-container">
      <div className="admin-group-create-project-main-content">
        {/* Project Information */}
        <div className="admin-group-create-project-card">
          <div className="admin-group-create-project-card-header">
            <svg className="admin-group-create-project-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h2>Project Information</h2>
          </div>

          <div className="admin-group-create-project-form-group">
            <label htmlFor="projectTitle" className="admin-group-create-project-label">
              Project Title <span className="admin-group-create-project-required">*</span>
            </label>
            <input
              id="projectTitle"
              type="text"
              className="admin-group-create-project-input"
              placeholder="e.g., Customer Service Excellence Initiative"
              value={projectTitle}
              onChange={(e) => setProjectTitle(e.target.value)}
            />
          </div>

          <div className="admin-group-create-project-form-group">
            <label htmlFor="description" className="admin-group-create-project-label">Description / Instructions</label>
            <textarea
              id="description"
              className="admin-group-create-project-textarea"
              placeholder="Describe the project objectives and requirements..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </div>

          <div className="admin-group-create-project-form-row">
            <div className="admin-group-create-project-form-group">
              <label htmlFor="category" className="admin-group-create-project-label">Category</label>
              <select
                id="category"
                className="admin-group-create-project-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">Select</option>
                <option value="development">Development</option>
                <option value="design">Design</option>
                <option value="marketing">Marketing</option>
                <option value="research">Research</option>
              </select>
            </div>

            <div className="admin-group-create-project-form-group">
              <label htmlFor="deadline" className="admin-group-create-project-label">
                Deadline <span className="admin-group-create-project-required">*</span>
              </label>
              <input
                id="deadline"
                type="date"
                className="admin-group-create-project-input"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Resources */}
        <div className="admin-group-create-project-card">
          <div className="admin-group-create-project-card-header">
            <svg className="admin-group-create-project-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
            <h2>Project Resources</h2>
          </div>

          <button className="admin-group-create-project-upload-button">
            <svg className="admin-group-create-project-icon-small" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Upload Resources (PDF, docs, links)
          </button>
        </div>

        {/* Deliverables */}
        <div className="admin-group-create-project-card">
          <div className="admin-group-create-project-card-header">
            <svg className="admin-group-create-project-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h2>Define Deliverables</h2>
          </div>

          <div className="admin-group-create-project-deliverables-list">
            {deliverables.map((deliverable, index) => (
              <input
                key={deliverable.id}
                type="text"
                className="admin-group-create-project-input"
                placeholder={`Deliverable ${index + 1}`}
                value={deliverable.value}
                onChange={(e) => updateDeliverable(deliverable.id, e.target.value)}
              />
            ))}
          </div>

          <button className="admin-group-create-project-add-button" onClick={addDeliverable}>
            <span className="admin-group-create-project-plus">+</span>
            Add Deliverable
          </button>
        </div>

        {/* Team Settings */}
        <div className="admin-group-create-project-card">
          <div className="admin-group-create-project-card-header">
            <svg className="admin-group-create-project-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h2>Team Settings</h2>
          </div>

          <div className="admin-group-create-project-form-group">
            <label htmlFor="teamCreation" className="admin-group-create-project-label">Team Creation</label>
            <select id="teamCreation" className="admin-group-create-project-select">
              <option value="automatic">Automatic System Assign</option>
              <option value="manual">Manual Assignment</option>
            </select>
          </div>

          <div className="admin-group-create-project-form-group">
            <label htmlFor="teamSize" className="admin-group-create-project-label">Team Size</label>
            <input
              id="teamSize"
              type="number"
              className="admin-group-create-project-input"
              value={teamSize}
              onChange={(e) => setTeamSize(e.target.value)}
              min="1"
              max="20"
            />
          </div>
        </div>
      </div>

      {/* Sidebar Actions */}
      <div className="admin-group-create-project-sidebar">
        <div className="admin-group-create-project-actions-card">
          <h3 className="admin-group-create-project-actions-title">Actions</h3>

          <button className="admin-group-create-project-btn admin-group-create-project-btn-primary" onClick={handlePublish}>
            <svg className="admin-group-create-project-icon-small" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Publish Project
          </button>

          <button className="admin-group-create-project-btn admin-group-create-project-btn-secondary" onClick={handleSaveDraft}>
            <svg className="admin-group-create-project-icon-small" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            Save as Draft
          </button>

          <button className="admin-group-create-project-btn admin-group-create-project-btn-ghost" onClick={handleCancel}>
            <svg className="admin-group-create-project-icon-small" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectForm;
